export enum InputType { text = 'text', date = 'date', password = 'password'}

export enum AccountType {
    free = 'free',
    starter = 'starter',
    standard = 'standard',
    pro = 'pro',
}

export interface LoginPayload {
    username: string;
    password: string;
}

export interface ReservationPayload {
    id?: string;
    first_name: string;
    last_name: string;
    note?: string;
    email: string;
    phone: string;
    barber: string;
    service: string;
    date: string;
    agreed_terms?: boolean;
    agreed_marketing?: boolean;
}

export interface RegisterPayload {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    password: string;
    barber_shop: RegisterBarberShopPayload;
    language: string
}

export interface RegisterBarberShopPayload {
    name: string;
    legal_name: string;
    public_email: string;
    public_number: string;
    // logo: string | null; //later
    account_type: AccountType;
    city?: string;
}

export type FormValues = { [key: string]: any };

export type ServiceCategoryKind = 'main' | 'sub'

export interface ServiceCategoryPayload extends FormValues {
    name: string;
    service_title?: string | null;
    active?: boolean;
    kind?: ServiceCategoryKind;
    parents?: string[];
}

export interface ServiceCategory {
    id: string;
    name: string;
    service_title: string | null;
    active: boolean;
    // BE #23: 4 mains (Hair/Beard/Combo/Other) + 12 subs (Cut, Skin Fade, …).
    // Subs reference their parent mains via `parents`.
    kind: ServiceCategoryKind;
    parents: string[];
}

export interface ServicePayload extends FormValues {
    name: string;
    description: string|null;
    duration: number|null;
    price: number|null;
    serviceCategory: string | null;
    maxCapacity?: number|null;
    active?: boolean;
    // BE #23: free-text array of sub-category keys (drives Explore filters).
    subs?: string[];
}

export interface BarberService {
    id: string;
    barber: string;
    duration: number | null;
    price: number | null;
    service: string | null;
}

export interface Service {
    id: string;
    name: string;
    description: string|null;
    duration: number|null;
    price: number|null;
    serviceCategory: string | null;
    maxCapacity?: number|null;
    active: boolean;
    category?: ServiceCategory | string | null;
    barber_services?: BarberService[];
    subs: string[];                 // BE #23 sub-category keys
    created_at?: string;
}

export interface BarberServicePayload extends FormValues {
    duration: number|null;
    price: number|null;
    service: string | null;
    barber: string | null;
}

// BE #24: sparse per-barber overrides on the shop catalogue. A row exists
// only when the calling barber diverges from shop default (price, duration,
// or offer flag). Resetting deletes the row → falls back to shop default.
export interface MyBarberServiceOverride {
    service_id: string;
    offer: boolean;
    price: number | null;     // null = inherit shop price
    dur: number | null;       // null = inherit shop duration
}

export interface MyBarberServiceUpsertPayload {
    offer: boolean;
    price?: number | null;
    dur?: number | null;
}

export enum Role {
    SuperAdmin = 'ROLE_SUPER_ADMIN',
    Admin = 'ROLE_ADMIN',
    Owner = 'ROLE_OWNER',
    Barber = 'ROLE_BARBER',
}

export const ADMIN_ROLES: ReadonlyArray<string> = [Role.Admin, Role.SuperAdmin]

export const hasAdminRole = (roles: ReadonlyArray<string> | undefined | null): boolean =>
    Array.isArray(roles) && roles.some((r) => ADMIN_ROLES.includes(r))

// ─── /me response ──────────────────────────────────────────────────
// Flat shape (FE #32 / BE #29). The authenticated user IS the barber in
// this codebase — there's no separate User entity — so per-barber
// fields (can_services, can_hours, active, position) live at the top
// level. `is_owner` is derived from `roles.includes(Role.Owner)`.
//
// `accent` is a wire alias for `primaryColor` on the entity.
// `coords` is derived client-side from `barber_shop.address.{latitude,longitude}`
// when convenience is wanted; no `coords` field exists on the wire.

export type OnboardingStep = 'profile' | 'services' | 'hours' | 'plan' | 'done'

export interface OnboardingChecklist {
    location: boolean
    team: boolean
    bookio: boolean
    share: boolean
}

export interface OnboardingState {
    onboarded: boolean
    step: OnboardingStep
    checklist: OnboardingChecklist
}

export interface OnboardingPatchPayload {
    step?: OnboardingStep
    checklist?: Partial<OnboardingChecklist>
}

export interface OnboardingServiceItem {
    name: string
    name_sk?: string
    price: number
    duration: number
    category: string
    subcategories: string[]
}

export interface OnboardingHoursRange {
    from: string
    to: string
}

export interface OnboardingHoursDay {
    open: boolean
    ranges: OnboardingHoursRange[]
}

export type OnboardingHoursWeek = Record<
    'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun',
    OnboardingHoursDay
>

export interface CurrentUserAddress {
    id: string
    line1: string | null
    line2: string | null
    post_code: string | null
    city: string | null
    country: string | null
    latitude: number | null
    longitude: number | null
}

// Client-side helper for components that pass lat/lng around (e.g. the
// onboarding map picker). NOT a wire field on /me — derive from
// barber_shop.address.{latitude,longitude} when reading server state.
export interface ShopCoords {
    lat: number
    lng: number
}

export const addressToCoords = (a: CurrentUserAddress | null | undefined): ShopCoords | null => {
    if (!a || a.latitude == null || a.longitude == null) return null
    return {lat: a.latitude, lng: a.longitude}
}

export interface CurrentUserBarberShop {
    id: string
    name: string
    legal_name?: string
    slug: string
    address: CurrentUserAddress | null
    phone?: string
    public_email?: string
    public_number?: string
    about?: string
    logo?: string | null
    accent: string                  // wire alias for primaryColor (FE #25)
    onboarded: boolean              // FE #24 reads this to gate the route
    account_type?: AccountType
    language: string                // real column, default 'en'
    // IANA tz id (e.g. "Europe/Bratislava"). Display anchor for owner/barber
    // surfaces (Dashboard, BarberPlanOfDay). Optional — FE falls back to
    // moment.tz.guess() when missing, but the BE should always send it.
    time_zone?: string
    hidden_from_explore?: boolean
    // Customer-tag permission (BE: tags v1, 2026-06-28). Owner-only PATCH;
    // when true, staff can create/rename/recolour/delete manual tags and
    // edit auto-rules. Assigning/removing always allowed.
    staff_can_manage_tags?: boolean
    // No-show defence policy (BE: tags v1, 2026-06-28). Mirrored to the
    // customer-profile NoShowDefenceCard. Owner-only writes via PATCH
    // /api/me/barber-shop.
    noshow_enabled?: boolean
    noshow_threshold?: number
    noshow_action?: 'warn' | 'deposit' | 'block'
    noshow_charge?: 'deposit' | 'full'
    noshow_deposit_pct?: number
}

export interface CurrentUser {
    id: string
    email: string
    first_name?: string
    last_name?: string
    phone_number?: string
    photo?: string | null
    roles: string[]
    // Per-barber fields — promoted to top level (User === Barber)
    can_services: boolean
    can_hours: boolean
    active: boolean
    position?: string
    barber_shop: CurrentUserBarberShop
    onboarding?: OnboardingState
}

export enum SortDirection {
    Asc = 'asc',
    Desc = 'desc',
}

export type SortValues = {
    sortField: string;
    sortDirection: SortDirection;
}

export interface Pagination<T> {
    data: T[];
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
}

// Newer BE list endpoints return the camelCase Pagination DTO envelope
// (2026-07 contract): /public/barbershops, /me/barber-shop/blocklist,
// /me/barber-shop/notifications/audit, /admin/*. Row shapes stay snake_case.
export interface PaginationCamel<T> {
    data: T[];
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
}

export enum EventType {
    Vacation = 'vacation',
    Reservation = 'reservation',
}

export enum ViewType {
    Current = 'current',
    Historical = 'historical',
}

export enum ReservationStatus {
    NotConfirmed = 'not_confirmed',
    Confirmed = 'confirmed',
    NoShow = 'no_show',
    Cancelled = 'cancelled',
    // WAITLIST.md §19.4 — soft-hold reservation created by the waitlist claim
    // endpoint. Counts as occupying for availability, distinct visual.
    PendingClaim = 'pending_claim',
}

export interface Reservation {
    id: string;
    service_title: string;
    barber: {
        id: string;
        visible_name: string;
    };
    title: string;
    customer_data: any[];
    duration: number;
    price?: number;
    start_date_time: string;
    end_date_time: string;
    customer: {
        id: string;
        first_name: string;
        last_name: string;
        email: string;
        phone: string;
        note: string | null;
    };
    agreed_terms: boolean;
    agreed_marketing: boolean;
    status: string;
    note: string | null;
    should_notify_customer?: boolean;
    created_at?: string;
}

export interface Barber {
    id: string;
    visible_name: string;
    email?: string;
    roles?: string[];
    first_name?: string;
    last_name?: string;
    phone_number?: string;
    rank?: string;
    position?: string;
    photo?: string | null;
    active?: boolean;
    // BE #27: owner-granted permission flags. Owner identity derives from roles.
    can_services?: boolean;
    can_hours?: boolean;
    created_at?: string;
    updated_at?: string;
    barberShop?: string;
    // BE #30: always present on the POST /barber response — a BarberInvite
    // when the new barber had an email, explicit null when no invite was
    // issued (2026-07 contract; don't use `'invite' in body` checks).
    // Absent on GET list rows and PUT/PATCH responses.
    invite?: BarberInvite | null;
}

// BE #30 — barber invite token. Single-use, expires 7 days after issuance.
// `accept_url` is `${FRONTEND_URL}/invite/accept?token=…`.
export interface BarberInvite {
    token: string;
    expires_at: string;
    accept_url: string;
}

export interface AcceptInvitePayload {
    token: string;
    password: string;
}

export interface AcceptInviteResponse {
    token: string;
    refresh_token: string;
    first_name?: string;
}

export interface BarberPayload extends FormValues {
    first_name: string;
    last_name: string;
    email?: string;
    phone_number?: string;
    position?: string;
    active?: boolean;
    can_services?: boolean;
    can_hours?: boolean;
}

export interface BarberPermissionsPayload {
    can_services?: boolean;
    can_hours?: boolean;
    active?: boolean;
}

export interface Vacation {
    id: string;
    name?: string;
    barber: {
        id: string;
        visible_name: string;
    };
    title: string;
    start_date_time: string;
    end_date_time: string;
    all_day?: boolean;
    note: string | null;
    created_at?: string;
    updated_at?: string;
}

export enum DateFormats {
    Date ='YYYY-MM-DD',
    DateTime ='YYYY-MM-DD HH:mm:ss',
    // FE #43 — wire format for datetimes sent to BE: UTC ISO 8601 with literal `Z`.
    // `[Z]` is moment's escape syntax for a literal char in the output.
    WireUtcDateTime ='YYYY-MM-DDTHH:mm:ss[Z]',
    CustomerDate ='dd DD.MM.YYYY',
    FnsCustomerDate ='EE dd.MM.yyyy',
    CustomerTimeNoSec ='HH:mm',
    CustomerDateTimeDayNoSec ='dd DD.MM.YYYY HH:mm',
    CustomerDateTimeNoSec ='DD.MM.YYYY HH:mm',
}

// NOT USED!!
//maybe approved and so on..
//  "Planned": "Planned",
//       "InProgress": "In Progress",
//       "Completed": "Completed",
//       "Cancelled": "Cancelled"
export enum VacationStatus {
}

// Subscription Types
export enum SubscriptionStatus {
    Trialing = 'trialing',
    Active = 'active',
    PastDue = 'past_due',
    Canceled = 'canceled',
    Incomplete = 'incomplete',
    Expired = 'expired',
    Free = 'free',
}

export interface SubscriptionCapabilities {
    bookings: number;
    staff: number;
    sms: number;
    smsOverageCents: number | null;
    explorePriority?: number;
    multiServices?: boolean;
    vacations?: boolean;
    reminder1h?: boolean;
    remindersCustom?: boolean;
    featured?: boolean;
    waitlist?: boolean;
    customDomain?: boolean;
    eshop?: boolean;
    whiteLabel?: boolean;
    stats?: string[];
}

export interface SubscriptionUsageBucket {
    used: number;
    limit: number;
}

export interface SubscriptionUsage {
    tier: string;
    stripeExempt: boolean;
    bookings: SubscriptionUsageBucket;
    staff: SubscriptionUsageBucket;
    sms: SubscriptionUsageBucket;
}

export interface SubscriptionStatusResponse {
    status: SubscriptionStatus | null;
    planType: AccountType | null;
    trialEndsAt: string | null;
    currentPeriodEnd: string | null;
    isStripeExempt: boolean;
    requiresPayment: boolean;
    canDowngradeToFree: boolean;
    daysLeftInTrial: number | null;
    cancelAtPeriodEnd?: boolean;
    // Whether a payment method is on file with Stripe. Optional until BE
    // ships it in /subscription/status — see docs/NOTIFICATIONS_BE.md.
    cardOnFile?: boolean;
    // BE plan-resolution helpers added alongside the legacy fields.
    tier?: string;
    capabilities?: SubscriptionCapabilities;
    usage?: SubscriptionUsage;
}

export type BillingInterval = 'monthly' | 'yearly';

export interface CheckoutRequest {
    planType?: string;
    billingInterval?: BillingInterval;
    successUrl?: string;
    cancelUrl?: string;
    // Server-side analytics attribution (sent to GA4 MP + Meta CAPI from Stripe webhooks)
    ga_client_id?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    fbc?: string;
    fbp?: string;
    analytics_consent?: boolean;
}

export interface PlanPricing {
    price: number;
    price_id: string;
}

export interface YearlyPlanPricing extends PlanPricing {
    monthly_equivalent: number;
    savings: number;
}

export interface PricingPlan {
    name: string;
    monthly: PlanPricing;
    yearly: YearlyPlanPricing;
}

export interface PricingResponse {
    starter: PricingPlan;
    standard: PricingPlan;
    pro: PricingPlan;
}

export interface CheckoutResponse {
    checkoutUrl: string;
    sessionId: string;
}

export interface PortalRequest {
    returnUrl?: string;
}

export interface PortalResponse {
    portalUrl: string;
}

export interface FreeTierUsageResponse {
    isFree: boolean;
    bookingsUsed: number;
    bookingsLimit: number;
    bookingsRemaining: number;
    percentageUsed: number;
}
